import { getCurrentUser } from 'aws-amplify/auth';
import { getSelfUsername } from './userSelf';

// Breakglass mode constant - set to true to enable breakglass mode
export const BREAKGLASSMODE = false;

// User-specific restrictions - users in this list have limited access
// They can ONLY view Reports and Agent Tree, no transactions, no password changes
export const RESTRICTED_USERS = ['countryiraq'];

/**
 * Check if current user is in restricted mode (read-only access to Reports and Agent Tree only)
 *
 * IMPORTANT: this must stay fast. It used to call getSelfUsername(), which
 * triggers the FULL user-tree fetch (multi-MB, seconds) — and since
 * RestrictedRouteGuard blocks every route on this check, the whole app showed
 * a blank/loading screen until the tree arrived. The username from Cognito
 * (local, instant) is all we need to check against the restricted list.
 * @returns {Promise<boolean>} True if current user is restricted, false otherwise
 */
export const isUserRestricted = async () => {
  try {
    let username;
    try {
      const currentUser = await getCurrentUser();
      username = (currentUser?.username || '').toLowerCase();
    } catch (e) {
      // Not signed in yet or Cognito unavailable; fall back to tree-based lookup
      username = await getSelfUsername();
    }
    const isRestricted = RESTRICTED_USERS.includes(username);
    
    if (isRestricted) {
      console.log(`🔒 User '${username}' is in RESTRICTED mode - read-only access to Reports and Agent Tree only`);
    }
    
    return isRestricted;
  } catch (error) {
    console.error('❌ Error checking user restriction:', error);
    return false;
  }
};

/**
 * Check if breakglass mode is active
 * Breakglass mode is active when:
 * 1. BREAKGLASSMODE constant is set to true
 * 2. Current user's username is 'playwinadmin'
 * 
 * @returns {Promise<boolean>} True if breakglass mode is active, false otherwise
 */
export const isBreakglassModeActive = async () => {
  try {
    // Check if BREAKGLASSMODE constant is enabled
    if (!BREAKGLASSMODE) {
      console.log('🔒 Breakglass mode disabled via constant');
      return false;
    }

    // Get the current user's username
    const username = await getSelfUsername();
    
    // Check if username is 'playwinadmin'
    if (username === 'playwinadmin') {
      console.log('🚨 Breakglass mode ACTIVE - playwinadmin detected');
      return true;
    }

    console.log(`🔒 Breakglass mode inactive - username is '${username}', not 'playwinadmin'`);
    return false;
  } catch (error) {
    console.error('❌ Error checking breakglass mode:', error);
    return false;
  }
};

/**
 * Check if current user is the breakglass admin
 * @returns {Promise<boolean>} True if current user is playwinadmin, false otherwise
 */
export const isBreakglassAdmin = async () => {
  try {
    const username = await getSelfUsername();
    return username === 'playwinadmin';
  } catch (error) {
    console.error('❌ Error checking breakglass admin:', error);
    return false;
  }
};

/**
 * Get breakglass mode status information
 * @returns {Promise<Object>} Object containing breakglass status details
 */
export const getBreakglassModeStatus = async () => {
  try {
    const username = await getSelfUsername();
    const isPlaywinAdmin = username === 'playwinadmin';
    const isActive = BREAKGLASSMODE && isPlaywinAdmin;

    return {
      active: isActive,
      constantEnabled: BREAKGLASSMODE,
      isBreakglassAdmin: isPlaywinAdmin,
      currentUsername: username,
      message: isActive 
        ? 'Breakglass mode is ACTIVE' 
        : !BREAKGLASSMODE 
          ? 'Breakglass mode disabled (constant is false)'
          : 'Breakglass mode disabled (not logged in as playwinadmin)'
    };
  } catch (error) {
    console.error('❌ Error getting breakglass status:', error);
    return {
      active: false,
      constantEnabled: BREAKGLASSMODE,
      isBreakglassAdmin: false,
      currentUsername: null,
      error: error.message,
      message: 'Error checking breakglass mode'
    };
  }
};

